import { useMutation } from 'react-query';

import { useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { openNotification } from '../components';

import type { PreviewEmailVisibility, UpdatePreviewEmailVisibilityEntity } from 'services/api/preview/ApiPreviewRepository';
import { DOCUMENT_PREVIEW_PERMISSIONS_QUERY_KEY } from './useGetDocumentPreviewEmailVisibility';
import { apiDocumentServiceRepository } from 'services/repositories/implementations/ApiDocumentServiceRepository';

export function useUpdateDocumentPreviewPermissions({ documentId, contentId }: { documentId: string; contentId: string }) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const queryKey = DOCUMENT_PREVIEW_PERMISSIONS_QUERY_KEY(documentId);

  const { mutateAsync: updatePreviewEmailVisibility } = useMutation({
    mutationFn: async (payload: UpdatePreviewEmailVisibilityEntity) => {
      const documentServiceResult = await apiDocumentServiceRepository.patchContent({
        contentId,
        payload: {
          content: {
            hasEmailVisibility: payload.has_email_visibility,
          },
        },
      });

      if (!documentServiceResult) {
        throw new Error(`Failed to persist viewer identification setting for contentId: ${contentId}`);
      }
    },
    onMutate: async (payload: UpdatePreviewEmailVisibilityEntity) => {
      await queryClient.cancelQueries({ queryKey });
      const previousValue = queryClient.getQueryData<PreviewEmailVisibility>(queryKey);
      queryClient.setQueryData<PreviewEmailVisibility>(queryKey, payload);
      return { previousValue };
    },
    onError: (_error, _payload, context) => {
      if (context?.previousValue) {
        queryClient.setQueryData(queryKey, context.previousValue);
      }
      openNotification({
        type: 'error',
        title: t('share_document_v3.viewer_identification_card.update_error'),
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  return {
    updatePreviewEmailVisibility,
  };
}
